import { reactive } from '@vue/composition-api';
import { merge } from 'lodash';
import axios from '@/services/axios';
import { toLocaleString, toFixedWithOutRounding, storageData } from '@/common/util';
import { getKConfData,  deserializationJSON } from '@/config/fish/index';

interface InviteShareInfo {
    result: number,
    data: {
        headUrl: string,
        nickName: string,
        inviteCode: string,
        landingBindAward: number,
        landingTotalAward: number,
        currencySymbol: string,
    }
}

interface InviteShareData {
    result: number,
    landingBindAward: string,
    landingTotalAward: string,
    coinUnit: string,
    headUrl: string,
    nickName: string,
    inviteCode: string,
    sharePageType: number
}

interface LandingParams {
    visitor: string | (string | null)[],
}
interface MessagesOption {
    path: string,
    country: string,
    language: string
}
interface FishParams {
    fishId: number,
    language: string,
    isSv?: boolean,
    pageName?: string
}

interface MaterialInfo {
    name: string,
    desc: string,
    shareCaption: string,
    imageUrl: string,
    h5Style: {
        sharePageType: string,
        pageConfig: object,
    },
};
interface FishInfo {
    id: number,
    content: MaterialInfo
};

interface FishInfoRes {
    result: number,
    data: FishInfo,
};

interface ReportParams {
    reportType: string,
    data: string,
}

enum LanguageStorage {
    name='LANGUAGE_DATA',
    time='LANGUAGE_TIME',
};

enum MessagesStorage {
    name='MESSAGES_DATA',
    time='MESSAGES_TIME',
}

const inviteShareData = reactive<InviteShareData>({
    result: 0,
    landingBindAward: '0',
    landingTotalAward: '0',
    coinUnit: '',
    headUrl: '',
    nickName: '',
    inviteCode: '',
    sharePageType: 0,
});

const fetchData = async (params: LandingParams) => {
    const res: InviteShareInfo = await axios.get('/rest/w/promotion/task/relation/getRelationLanding', {
        params,
    });
    inviteShareData.result = res.result;
    if (inviteShareData.result === 1) {
        const {
            headUrl,
            nickName,
            inviteCode,
            currencySymbol,
            landingBindAward = 0,
            landingTotalAward = 0,
        } = res.data;
        inviteShareData.landingBindAward = toLocaleString(toFixedWithOutRounding(Number(landingBindAward) / 100, 2));
        inviteShareData.landingTotalAward = toLocaleString(toFixedWithOutRounding(Number(landingTotalAward) / 100, 2));
        inviteShareData.coinUnit = currencySymbol || '';
        inviteShareData.headUrl = String(headUrl).replace('http:', '') || '';
        inviteShareData.nickName = nickName || '';
        inviteShareData.inviteCode = inviteCode || '';
    }
};

const reportShareTask = async (reportParams: ReportParams) => {
    const formData = new FormData();
    formData.append('reportType', reportParams.reportType as string);
    formData.append('data', reportParams.data as string);
    return await axios.post('/rest/w/promotion/task/share/report',
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
};

// 通过kconf获取落地页样式和素材

const getMessagesByCountry = async (options: MessagesOption) => {
    const settingsRedisTime = 120;
    let projectSettings:any = {};
    const {data, isExpired} = storageData(LanguageStorage.name, `${LanguageStorage.name}_TIME`, settingsRedisTime);
    if (data) {
        projectSettings = deserializationJSON(data);
    }
    // 如果过期或数据为空，重新请求
    if (isExpired || !data || !projectSettings) {
        const projectSettingsKey = 'X6Frontend.kwai-pro-settings.encourage-project-settings';
        const projData = await getKConfData([projectSettingsKey]);
        projectSettings = deserializationJSON(projData[projectSettingsKey]);
        window.localStorage.setItem(LanguageStorage.name, projData[projectSettingsKey]);
        window.localStorage.setItem(`${LanguageStorage.name}_TIME`, new Date().getTime().toString());
    }

    // 若获取失败 发起报警
    if (!projectSettings) {
        return {};
    }

    const { countries, pages } = projectSettings;

    // 有可能为空
    const currentCountry = countries.find((item: { key: string; }) => item.key === options.country);

    if (!currentCountry) {
        return {};
    }
    // 默认语言
    const defaultLanguage = currentCountry.defaultLanguage;

    const langMessagesFileConf = pages.find((item: { path: string; }) => item.path === options.path)?.langMessages ?? [];

    const langMessageFiles = langMessagesFileConf.reduce((languageMessageFiles: string[], item: { filePathPrefix: any; }) => {
        // 1. 兼容处理
        if (options.country === 'br') {
            languageMessageFiles.push(`${item.filePathPrefix}-${defaultLanguage}`);
        }

        // 2. 获取默认配置
        languageMessageFiles.push(`${item.filePathPrefix}-${options.country}-${defaultLanguage}`);

        // 当前语言不为空 并且请求语言与当前桶默认语言不相同时 获取默认语言和请求语言的两个配置
        if (options.language && defaultLanguage !== options.language) {
            // 3. 获取不带地区码的语言配置
            if (options.language.includes('-')) {
                const languageWithoutOrigin = options.language.split('-')[0];
                languageMessageFiles.push(`${item.filePathPrefix}-${options.country}-${languageWithoutOrigin}`);
            }

            languageMessageFiles.push(`${item.filePathPrefix}-${options.country}-${options.language}`);
        }

        return languageMessageFiles;
    }, [] as string[]);

    const redisTime = 60;
    let langMessages:any = {};
    const { data: messagesData, isExpired: messagesIsExpired} = storageData(`${options.path}_name`, `${options.path}_time`, redisTime);

    if (messagesData) {
        langMessages =  deserializationJSON(messagesData);
    }

    // 如果过期或数据为空，重新请求
    if (messagesIsExpired || !messagesData || !langMessages) {
        const kconfData = await getKConfData(langMessageFiles);
        const langMessageRequest = langMessageFiles.map((item: string ) => {
            if (kconfData[item]) {
                return deserializationJSON(kconfData[item]) ;
            }
        });
        const pageLangMessages = await Promise.all(langMessageRequest);
        langMessages =  merge(...pageLangMessages);
        window.localStorage.setItem(`${options.path}_name`, JSON.stringify(langMessages));
        window.localStorage.setItem(`${options.path}_time`, new Date().getTime().toString());
    }
    return langMessages;
};

// 通过后端接口获取落地页样式和素材
const getMessagesById = async (params: FishParams) => {
    let messages = {};
    try {
        const fishInfoRes: FishInfoRes = await axios.get('/rest/w/promotion/fish/getById', {
            params,
        });
        const { data, result } = fishInfoRes;
        if (result === 1) {
            let shareConfig: any = {};
            let key = '';
            const { pageConfig, sharePageType } = data.content.h5Style;
            shareConfig = pageConfig;
            const pageStyleMap: any = {
                1: 'experiment-1',
                2: 'experiment-2',
            };
            if ( sharePageType !== '0') {
                // 兜底snackvideo流量无sharePageType参数
                params.isSv && (inviteShareData.sharePageType = +sharePageType);
                key = pageStyleMap[sharePageType];
                shareConfig[key] = {
                    ...pageConfig,
                };
            }
            messages = {
                [params.pageName || 'inviteShare']: shareConfig,
            };
        }
    } catch (err) {
        console.log(err);
    }

    return messages;

};

export {
    inviteShareData,
    fetchData,
    getMessagesByCountry,
    getMessagesById,
    reportShareTask
};
